import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, 
  HelpCircle, 
  User, 
  Scan, 
  ShoppingCart, 
  Users, 
  BarChart3,
  ChevronDown,
  ChevronUp,
  Smartphone,
  LogIn,
  ClipboardList,
  Pill,
  Bell,
  Star
} from 'lucide-react';

const HelpPage = ({ onBack }) => {
  const { language } = useLanguage();
  const [openSection, setOpenSection] = useState('connexion');

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const sections = [
    {
      id: 'installation',
      icon: Smartphone,
      title: language === 'fr' ? 'Installation sur l\'écran d\'accueil' : 'Add to Home Screen',
      content: language === 'fr' ? (
        <div className="space-y-3">
          <p className="font-medium">iPhone (Safari) :</p>
          <ol className="list-decimal list-inside space-y-1 text-sm text-[#78716C]">
            <li>Ouvrez l'app dans Safari</li>
            <li>Tapez sur l'icône de partage (carré avec flèche)</li>
            <li>Sélectionnez "Sur l'écran d'accueil"</li>
          </ol>
          <p className="font-medium mt-4">Android (Chrome) :</p>
          <ol className="list-decimal list-inside space-y-1 text-sm text-[#78716C]">
            <li>Ouvrez l'app dans Chrome</li>
            <li>Tapez sur les 3 points (menu)</li>
            <li>Sélectionnez "Ajouter à l'écran d'accueil"</li>
          </ol>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="font-medium">iPhone (Safari):</p>
          <ol className="list-decimal list-inside space-y-1 text-sm text-[#78716C]">
            <li>Open the app in Safari</li>
            <li>Tap the share icon (square with arrow)</li>
            <li>Select "Add to Home Screen"</li>
          </ol>
          <p className="font-medium mt-4">Android (Chrome):</p>
          <ol className="list-decimal list-inside space-y-1 text-sm text-[#78716C]">
            <li>Open the app in Chrome</li>
            <li>Tap the 3 dots (menu)</li>
            <li>Select "Add to Home Screen"</li>
          </ol>
        </div>
      )
    },
    {
      id: 'connexion',
      icon: LogIn,
      title: language === 'fr' ? 'Connexion' : 'Login',
      content: language === 'fr' ? (
        <div className="space-y-3">
          <p className="font-medium">Pour se connecter :</p>
          <ol className="list-decimal list-inside space-y-1 text-sm text-[#78716C]">
            <li>Cliquez sur "Commencer le diagnostic"</li>
            <li>Entrez votre numéro de téléphone</li>
            <li>Entrez votre mot de passe</li>
            <li>Cliquez sur "Se connecter"</li>
          </ol>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Auto-inscription Agent :</strong> Utilisez le code pharmacie <strong>DERMASCAN2024</strong>
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="font-medium">To login:</p>
          <ol className="list-decimal list-inside space-y-1 text-sm text-[#78716C]">
            <li>Click "Start diagnosis"</li>
            <li>Enter your phone number</li>
            <li>Enter your password</li>
            <li>Click "Login"</li>
          </ol>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Agent self-registration:</strong> Use pharmacy code <strong>DERMASCAN2024</strong>
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'diagnostic',
      icon: Scan,
      title: language === 'fr' ? 'Créer un Diagnostic' : 'Create a Diagnosis',
      content: language === 'fr' ? (
        <div className="space-y-3">
          <p className="font-medium">Étape 1 : Cliquez sur "Nouveau diagnostic"</p>
          
          <p className="font-medium mt-3">Étape 2 : Choisissez la méthode</p>
          <ul className="list-disc list-inside text-sm text-[#78716C] space-y-1">
            <li><strong>Questionnaire</strong> : 5 questions rapides (recommandé)</li>
            <li><strong>Scan caméra</strong> : Analyse IA de la peau</li>
          </ul>
          
          <p className="font-medium mt-3">Étape 3 : Questions du questionnaire</p>
          <ol className="list-decimal list-inside text-sm text-[#78716C] space-y-1">
            <li>Type de peau (Normale/Sèche/Grasse/Mixte)</li>
            <li>Présence d'acné ?</li>
            <li>Présence de taches ?</li>
            <li>Peau sensible ?</li>
            <li>Tranche d'âge</li>
          </ol>
          
          <p className="font-medium mt-3">Étape 4 : Résultats</p>
          <ul className="list-disc list-inside text-sm text-[#78716C] space-y-1">
            <li>Score de peau (0-100)</li>
            <li>Routine matin & soir</li>
            <li>Soins hebdomadaires (gommage/masque)</li>
            <li>Compléments alimentaires recommandés</li>
          </ul>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="font-medium">Step 1: Click "New diagnosis"</p>
          
          <p className="font-medium mt-3">Step 2: Choose method</p>
          <ul className="list-disc list-inside text-sm text-[#78716C] space-y-1">
            <li><strong>Questionnaire</strong>: 5 quick questions (recommended)</li>
            <li><strong>Camera scan</strong>: AI skin analysis</li>
          </ul>
          
          <p className="font-medium mt-3">Step 3: Questionnaire</p>
          <ol className="list-decimal list-inside text-sm text-[#78716C] space-y-1">
            <li>Skin type (Normal/Dry/Oily/Combination)</li>
            <li>Acne present?</li>
            <li>Dark spots present?</li>
            <li>Sensitive skin?</li>
            <li>Age range</li>
          </ol>
          
          <p className="font-medium mt-3">Step 4: Results</p>
          <ul className="list-disc list-inside text-sm text-[#78716C] space-y-1">
            <li>Skin score (0-100)</li>
            <li>Morning & evening routine</li>
            <li>Weekly care (scrub/mask)</li>
            <li>Recommended supplements</li>
          </ul>
        </div>
      )
    },
    {
      id: 'vente',
      icon: ShoppingCart,
      title: language === 'fr' ? 'Vendre des Produits' : 'Sell Products',
      content: language === 'fr' ? (
        <div className="space-y-3">
          <p className="font-medium">1. Cliquez sur "Voir les produits"</p>
          
          <p className="font-medium mt-3">2. Onglet Cosmétiques</p>
          <ul className="list-disc list-inside text-sm text-[#78716C] space-y-1">
            <li>Parcourez ou recherchez les produits</li>
            <li>Filtrez par catégorie ou préoccupation</li>
            <li>Cliquez sur "+ Ajouter" pour mettre au panier</li>
          </ul>
          
          <p className="font-medium mt-3">3. Onglet Compléments</p>
          <ul className="list-disc list-inside text-sm text-[#78716C] space-y-1">
            <li>Voir les recommandés en haut</li>
            <li>Filtrez par type (Zinc, Vitamine C...)</li>
            <li>La durée de cure est indiquée</li>
          </ul>
          
          <p className="font-medium mt-3">4. Valider la vente</p>
          <ul className="list-disc list-inside text-sm text-[#78716C] space-y-1">
            <li>Vérifiez le total dans le panier</li>
            <li>Cochez "Relance auto renouvellement"</li>
            <li>Cliquez sur "Valider la vente"</li>
            <li>Téléchargez le PDF pour le client</li>
          </ul>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="font-medium">1. Click "View products"</p>
          
          <p className="font-medium mt-3">2. Cosmetics tab</p>
          <ul className="list-disc list-inside text-sm text-[#78716C] space-y-1">
            <li>Browse or search products</li>
            <li>Filter by category or concern</li>
            <li>Click "+ Add" to add to cart</li>
          </ul>
          
          <p className="font-medium mt-3">3. Supplements tab</p>
          <ul className="list-disc list-inside text-sm text-[#78716C] space-y-1">
            <li>See recommended at top</li>
            <li>Filter by type (Zinc, Vitamin C...)</li>
            <li>Cure duration is shown</li>
          </ul>
          
          <p className="font-medium mt-3">4. Validate sale</p>
          <ul className="list-disc list-inside text-sm text-[#78716C] space-y-1">
            <li>Check total in cart</li>
            <li>Check "Auto renewal reminder"</li>
            <li>Click "Validate sale"</li>
            <li>Download PDF for client</li>
          </ul>
        </div>
      )
    },
    {
      id: 'complements',
      icon: Pill,
      title: language === 'fr' ? 'Compléments Alimentaires' : 'Dietary Supplements',
      content: language === 'fr' ? (
        <div className="space-y-3">
          <p className="text-sm text-[#78716C]">Les compléments sont recommandés selon le type de peau :</p>
          
          <div className="space-y-2 mt-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="w-24 font-medium">Zinc</span>
              <span className="text-[#78716C]">→ Peau grasse, acné</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-24 font-medium">Vitamine C</span>
              <span className="text-[#78716C]">→ Taches, éclat</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-24 font-medium">Vitamine E</span>
              <span className="text-[#78716C]">→ Peau sèche, anti-âge</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-24 font-medium">Collagène</span>
              <span className="text-[#78716C]">→ Rides, fermeté</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-24 font-medium">Oméga-3</span>
              <span className="text-[#78716C]">→ Hydratation</span>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-green-50 rounded-lg">
            <p className="text-sm text-green-700">
              <strong>Durée de cure :</strong> Indiquée sur chaque produit (30-90 jours). 
              La relance automatique vous rappelle 7 jours avant la fin.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-[#78716C]">Supplements are recommended based on skin type:</p>
          
          <div className="space-y-2 mt-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="w-24 font-medium">Zinc</span>
              <span className="text-[#78716C]">→ Oily skin, acne</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-24 font-medium">Vitamin C</span>
              <span className="text-[#78716C]">→ Dark spots, radiance</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-24 font-medium">Vitamin E</span>
              <span className="text-[#78716C]">→ Dry skin, anti-aging</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-24 font-medium">Collagen</span>
              <span className="text-[#78716C]">→ Wrinkles, firmness</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-24 font-medium">Omega-3</span>
              <span className="text-[#78716C]">→ Hydration</span>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-green-50 rounded-lg">
            <p className="text-sm text-green-700">
              <strong>Cure duration:</strong> Shown on each product (30-90 days). 
              Auto reminder alerts you 7 days before end.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'score',
      icon: Star,
      title: language === 'fr' ? 'Comprendre le Score de Peau' : 'Understanding Skin Score',
      content: language === 'fr' ? (
        <div className="space-y-3">
          <p className="text-sm text-[#78716C]">Le score indique l'état de santé global de la peau (0-100) :</p>
          
          <div className="space-y-2 mt-3">
            <div className="flex items-center gap-3 p-2 bg-green-50 rounded-lg">
              <span className="text-2xl">🟢</span>
              <div>
                <p className="font-medium">80-100 : Excellent</p>
                <p className="text-xs text-[#78716C]">Peau en très bonne santé</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 bg-yellow-50 rounded-lg">
              <span className="text-2xl">🟡</span>
              <div>
                <p className="font-medium">60-79 : Bon</p>
                <p className="text-xs text-[#78716C]">Quelques préoccupations mineures</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 bg-orange-50 rounded-lg">
              <span className="text-2xl">🟠</span>
              <div>
                <p className="font-medium">40-59 : Moyen</p>
                <p className="text-xs text-[#78716C]">Préoccupations à traiter</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 bg-red-50 rounded-lg">
              <span className="text-2xl">🔴</span>
              <div>
                <p className="font-medium">20-39 : À améliorer</p>
                <p className="text-xs text-[#78716C]">Plusieurs problèmes détectés</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-[#78716C]">The score indicates overall skin health (0-100):</p>
          
          <div className="space-y-2 mt-3">
            <div className="flex items-center gap-3 p-2 bg-green-50 rounded-lg">
              <span className="text-2xl">🟢</span>
              <div>
                <p className="font-medium">80-100: Excellent</p>
                <p className="text-xs text-[#78716C]">Very healthy skin</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 bg-yellow-50 rounded-lg">
              <span className="text-2xl">🟡</span>
              <div>
                <p className="font-medium">60-79: Good</p>
                <p className="text-xs text-[#78716C]">Minor concerns</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 bg-orange-50 rounded-lg">
              <span className="text-2xl">🟠</span>
              <div>
                <p className="font-medium">40-59: Average</p>
                <p className="text-xs text-[#78716C]">Concerns to address</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 bg-red-50 rounded-lg">
              <span className="text-2xl">🔴</span>
              <div>
                <p className="font-medium">20-39: Needs improvement</p>
                <p className="text-xs text-[#78716C]">Multiple issues detected</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'clients',
      icon: Users,
      title: language === 'fr' ? 'Gérer les Clients' : 'Manage Clients',
      content: language === 'fr' ? (
        <div className="space-y-3">
          <p className="font-medium">Accès : Menu "Clients" (icône en haut à droite)</p>
          
          <ul className="list-disc list-inside text-sm text-[#78716C] space-y-2 mt-3">
            <li>Voir la liste de tous vos clients</li>
            <li>Consulter l'historique des achats</li>
            <li>Programmer des relances</li>
            <li>Prendre des RDV de suivi</li>
          </ul>
          
          <div className="mt-4 p-3 bg-amber-50 rounded-lg">
            <p className="text-sm text-amber-700">
              <strong>Relances :</strong> Les clients dont les produits arrivent à terme 
              apparaissent dans "Clients à relancer" sur votre tableau de bord.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="font-medium">Access: "Clients" menu (icon top right)</p>
          
          <ul className="list-disc list-inside text-sm text-[#78716C] space-y-2 mt-3">
            <li>View all your clients</li>
            <li>Check purchase history</li>
            <li>Schedule reminders</li>
            <li>Book follow-up appointments</li>
          </ul>
          
          <div className="mt-4 p-3 bg-amber-50 rounded-lg">
            <p className="text-sm text-amber-700">
              <strong>Reminders:</strong> Clients whose products are running out 
              appear in "Clients to contact" on your dashboard.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'stats',
      icon: BarChart3,
      title: language === 'fr' ? 'Statistiques (Admin)' : 'Statistics (Admin)',
      content: language === 'fr' ? (
        <div className="space-y-3">
          <p className="font-medium">Menu "Stats" en bas de l'écran</p>
          
          <ul className="list-disc list-inside text-sm text-[#78716C] space-y-2 mt-3">
            <li>Chiffre d'affaires global</li>
            <li>Statistiques par agent</li>
            <li>Classement des agents</li>
            <li>Définir les objectifs mensuels</li>
            <li>Exporter les données en Excel</li>
          </ul>
          
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Objectifs :</strong> Les agents voient leur progression 
              vers l'objectif avec des paliers à 50%, 75% et 100%.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="font-medium">"Stats" menu at bottom of screen</p>
          
          <ul className="list-disc list-inside text-sm text-[#78716C] space-y-2 mt-3">
            <li>Global revenue</li>
            <li>Per-agent statistics</li>
            <li>Agent ranking</li>
            <li>Set monthly targets</li>
            <li>Export data to Excel</li>
          </ul>
          
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Targets:</strong> Agents see their progress 
              toward goals with milestones at 50%, 75% and 100%.
            </p>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="bg-white border-b border-[#E7E5E4] sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-[#78716C] hover:text-[#0F172A] transition-colors"
            data-testid="help-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <HelpCircle className="w-6 h-6 text-[#0EA5E9]" />
            <h1 className="text-lg font-semibold text-[#0F172A]">
              {language === 'fr' ? 'Aide & Mode d\'emploi' : 'Help & User Guide'}
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-3">
        {/* App Name */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-[#0F172A]">SADASkinCare</h2>
          <p className="text-sm text-[#78716C]">
            {language === 'fr' 
              ? 'Diagnostic de peau spécialisé peau noire' 
              : 'Skin diagnosis specialized for black skin'}
          </p>
        </div>

        {/* Accordion Sections */}
        {sections.map((section) => (
          <Card 
            key={section.id} 
            className={`border transition-all duration-200 ${
              openSection === section.id ? 'border-[#0EA5E9] shadow-md' : 'border-[#E7E5E4]'
            }`}
          >
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full p-4 flex items-center justify-between text-left"
              data-testid={`help-section-${section.id}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  openSection === section.id ? 'bg-[#0EA5E9] text-white' : 'bg-[#F5F5F4] text-[#78716C]'
                }`}>
                  <section.icon className="w-5 h-5" />
                </div>
                <span className={`font-medium ${
                  openSection === section.id ? 'text-[#0EA5E9]' : 'text-[#0F172A]'
                }`}>
                  {section.title}
                </span>
              </div>
              {openSection === section.id ? (
                <ChevronUp className="w-5 h-5 text-[#0EA5E9]" />
              ) : (
                <ChevronDown className="w-5 h-5 text-[#78716C]" />
              )}
            </button>
            
            {openSection === section.id && (
              <CardContent className="pt-0 pb-4 px-4">
                <div className="pl-13 border-t border-[#E7E5E4] pt-4">
                  {section.content}
                </div>
              </CardContent>
            )}
          </Card>
        ))}

        {/* Contact Support */}
        <Card className="border-[#E7E5E4] mt-6">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-[#78716C]">
              {language === 'fr' 
                ? 'Besoin d\'aide supplémentaire ?' 
                : 'Need more help?'}
            </p>
            <p className="text-sm font-medium text-[#0F172A] mt-1">
              {language === 'fr' 
                ? 'Contactez votre administrateur' 
                : 'Contact your administrator'}
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default HelpPage;
